import { useState } from 'react';

import { Text, YStack } from '@/components/ui';
import { XStack } from '@/components/ui/layout/layout';

function AllNoneToggle({
  onAll,
  onNone,
}: {
  onAll: () => void;
  onNone: () => void;
}) {
  return (
    <XStack className="items-center gap-1.5">
      <button
        onClick={onAll}
        className="text-xs text-foreground-muted hover:text-foreground transition-colors"
      >
        all
      </button>
      <span className="text-foreground-muted opacity-40 text-xs">/</span>
      <button
        onClick={onNone}
        className="text-xs text-foreground-muted hover:text-foreground transition-colors"
      >
        none
      </button>
    </XStack>
  );
}

function SectionHeader({
  label,
  isExpanded,
  onToggle,
  toggleSlot,
}: {
  label: string;
  isExpanded: boolean;
  onToggle: () => void;
  toggleSlot: React.ReactNode;
}) {
  return (
    // No bottom margin here — sections apply their own top margin on the
    // content so that collapsed headers have equal padding above and below.
    <XStack className="items-center justify-between">
      <button
        onClick={onToggle}
        className="flex items-center gap-1.5 text-left group"
      >
        <span className="text-foreground-muted text-[11px] leading-none group-hover:text-foreground transition-colors">
          {isExpanded ? '▾' : '▸'}
        </span>
        <span className="text-xs font-semibold uppercase tracking-widest text-foreground-muted select-none group-hover:text-foreground transition-colors">
          {label}
        </span>
      </button>
      {toggleSlot}
    </XStack>
  );
}

export function GraphLegend({
  colorMap,
  hiddenTypes,
  onToggleType,
  onSelectAllTypes,
  onDeselectAllTypes,
  semanticTypes,
  hiddenSemantics,
  onToggleSemantic,
  onSelectAllSemantics,
  onDeselectAllSemantics,
  searchQuery,
  matchCount,
  onSearchChange,
}: {
  colorMap: Record<string, string>;
  hiddenTypes: Set<string>;
  onToggleType: (type: string) => void;
  onSelectAllTypes: () => void;
  onDeselectAllTypes: () => void;
  semanticTypes: string[];
  hiddenSemantics: Set<string>;
  onToggleSemantic: (semantic: string) => void;
  onSelectAllSemantics: () => void;
  onDeselectAllSemantics: () => void;
  searchQuery: string;
  matchCount: number | null;
  onSearchChange: (query: string) => void;
}) {
  const [isEntitiesExpanded, setIsEntitiesExpanded] = useState(false);
  const [isRelationshipsExpanded, setIsRelationshipsExpanded] = useState(false);
  const entityEntries = Object.entries(colorMap);
  if (entityEntries.length === 0) return null;

  return (
    <div className="absolute top-3 left-3 flex flex-col bg-surface/80 border border-border rounded-lg backdrop-blur-sm overflow-hidden w-[280px] max-h-[70vh]">
      {/* Search row */}
      <div className="flex items-center gap-2 px-3.5 py-3 border-b border-border">
        <svg
          className="w-3.5 h-3.5 shrink-0 text-foreground-muted"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search entities…"
          className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-foreground-muted min-w-0"
        />
        {searchQuery && matchCount !== null && (
          <Text size="xs" lowContrast className="shrink-0 tabular-nums">
            {matchCount}
          </Text>
        )}
        {searchQuery && (
          <button
            onClick={() => onSearchChange('')}
            className="text-foreground-muted hover:text-foreground text-xs leading-none shrink-0"
            aria-label="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      {/* Scrollable body */}
      <div className="flex flex-col overflow-y-auto">
        {/* Entities section — equal py-3 top and bottom whether collapsed or not */}
        <div className="px-3.5 py-3">
          <SectionHeader
            label="Entities"
            isExpanded={isEntitiesExpanded}
            onToggle={() => setIsEntitiesExpanded((v) => !v)}
            toggleSlot={
              <AllNoneToggle
                onAll={onSelectAllTypes}
                onNone={onDeselectAllTypes}
              />
            }
          />
          {isEntitiesExpanded && (
            <YStack className="gap-3 mt-3">
              {entityEntries.map(([type, color]) => {
                const isHidden = hiddenTypes.has(type);
                return (
                  <button
                    key={type}
                    onClick={() => onToggleType(type)}
                    className="flex items-center gap-2.5 text-left w-full transition-opacity"
                    style={{ opacity: isHidden ? 0.35 : 1 }}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    <span
                      className="text-sm text-foreground leading-none select-none"
                      style={{
                        textDecoration: isHidden ? 'line-through' : 'none',
                      }}
                    >
                      {type}
                    </span>
                  </button>
                );
              })}
            </YStack>
          )}
        </div>

        {/* Relationships section — equal py-3 top and bottom whether collapsed or not */}
        {semanticTypes.length > 0 && (
          <div className="border-t border-border px-3.5 py-3">
            <SectionHeader
              label="Relationships"
              isExpanded={isRelationshipsExpanded}
              onToggle={() => setIsRelationshipsExpanded((v) => !v)}
              toggleSlot={
                <AllNoneToggle
                  onAll={onSelectAllSemantics}
                  onNone={onDeselectAllSemantics}
                />
              }
            />
            {isRelationshipsExpanded && (
              <YStack className="gap-3 mt-3">
                {semanticTypes.map((semantic) => {
                  const isHidden = hiddenSemantics.has(semantic);
                  return (
                    <button
                      key={semantic}
                      onClick={() => onToggleSemantic(semantic)}
                      className="flex items-center gap-2.5 text-left w-full transition-opacity"
                      style={{ opacity: isHidden ? 0.35 : 1 }}
                    >
                      <span className="w-3 h-px bg-foreground-muted shrink-0 inline-block" />
                      <span
                        className="text-sm text-foreground leading-none select-none font-mono"
                        style={{
                          textDecoration: isHidden ? 'line-through' : 'none',
                        }}
                      >
                        {semantic}
                      </span>
                    </button>
                  );
                })}
              </YStack>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
